import { useEffect } from 'react';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { Sidebar } from '@g17eco/molecules/sidebar';
import { QUESTION, SMART_MATCH } from '@constants/terminology';
import { AIAssistant } from './ai-assistant/AIAssistant';
import { Connections } from './connections/Connections';
import { Button } from 'reactstrap';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import { useConnectionContext } from './connections/ConnectionContext';
import { useTabs } from '@hooks/useTabs';
import { AnswerProps } from './ai-assistant/types';
import { DataIntegrations } from './data-integrations/DataIntegrations';
import { useAssistantPanelContext } from './AssistantPanelContext';
import { AssistantTabs } from './assistantTypes';

interface Props extends AnswerProps {
  open: boolean;
  toggle: () => void;
  canAccessAITools: boolean;
}

export const AssistantOffcanvas = ({ open, toggle, utrv, utr, initiativeUtr, canAccessAITools }: Props) => {
  const isUserStaff = useAppSelector(isStaff); // To remove later.

  const { removeConnection } = useConnectionContext();
  const { requestedTab, clearRequestedTab } = useAssistantPanelContext();

  const { currentTab, handleChangeTab } = useTabs({
    initialTab: AssistantTabs.Assistant,
    changeTabCallback: removeConnection,
  });

  useEffect(() => {
    if (!open || !requestedTab || requestedTab === currentTab) {
      return;
    }

    handleChangeTab(requestedTab);
    clearRequestedTab();
  }, [open, requestedTab, currentTab, handleChangeTab, clearRequestedTab]);

  return (
    <Sidebar
      isOpen={open}
      scrollable
      toggle={toggle}
      backdrop={false}
      header={canAccessAITools ? `${QUESTION.CAPITALIZED_SINGULAR} Assistant` : SMART_MATCH.CAPITALIZED_PLURAL}
      className='question-assistant shadow-lg'
      resizable
    >
      <div className='d-flex flex-column'>
        {canAccessAITools ? (
          <>
            {isUserStaff ? (
              <div className='mb-3 d-flex justify-content-between align-items-center'>
                {Object.values(AssistantTabs).map((tab) => (
                  <Button
                    className='flex-fill mr-2 text-nowrap'
                    key={tab}
                    color='primary'
                    outline={currentTab !== tab}
                    onClick={() => handleChangeTab(tab)}
                    active={currentTab === tab}
                  >
                    {tab}
                  </Button>
                ))}
                <FeatureStability className='ml-0' stability={'beta'} />
              </div>
            ) : null}
            {currentTab === AssistantTabs.Assistant ? <AIAssistant utr={utr} utrv={utrv} initiativeUtr={initiativeUtr} /> : null}
            {currentTab === AssistantTabs.SmartMatches ? <Connections utr={utr} utrv={utrv} /> : null}
            {currentTab === AssistantTabs.DataIntegrations ? <DataIntegrations utr={utr} utrv={utrv} /> : null}
          </>
        ) : (
          <Connections utr={utr} utrv={utrv} />
        )}
      </div>
    </Sidebar>
  );
};
